import { DISTANCE_UNITS } from "form/map/model"
import { Feature, FeatureCollection, LineString, MultiLineString, MultiPolygon, Point, Polygon } from "geojson"

const ALL_GEOMETRY_TYPES: GeoJSON.GeoJsonGeometryTypes[] = [
  "MultiPolygon",
  "Point",
  "MultiPoint",
  "LineString",
  "MultiLineString",
  "Polygon",
  "GeometryCollection"
]
export function getAllGeometryTypes(): GeoJSON.GeoJsonGeometryTypes[] {
  return ALL_GEOMETRY_TYPES.slice()
}

export enum GEOMETRY_UNITS {
  KILOMETERS = "kilometers",
  MILES = "miles",
  DEGREES = "degrees",
  RADIANS = "radians"
}

export type IPoint = [number, number] | [number, number, number]

export type ILine = IPoint[]

export type IPolygon = IPoint[]

export interface ILocation {
  x: number
  y: number
}

export interface IBoundaryBox {
  sw: ILocation
  ne: ILocation
}

export interface ICircle {
  center: ILocation
  /**
   * With mapbox the radius unit is kilometer
   */
  radius: number
}

export interface IGeometryService {
  checkLocation(location: ILocation): void
  isValidLocation(location: ILocation | undefined): boolean
  isValidBbox(bbox: IBoundaryBox | undefined): boolean
  isValidGeometry(geometry: any): boolean
  checkCircle(circle: ICircle): void
  checkPolygon(polygon: IPolygon): void
  checkLine(line: ILine): void
  checkBbox(bbox: IBoundaryBox): void
  getArea(feature: Feature): number
  getLineLength(
    feature: Feature<LineString> | Feature<MultiLineString>,
    units?: GEOMETRY_UNITS | DISTANCE_UNITS
  ): number
  getCentroid(feature: Feature | FeatureCollection): Feature<Point>
  getFeatureFromLine(line: ILine): Feature<LineString>
  getFeatureFromWkt(wkt: string): Feature
  getPolygonFeatureFromCircle(circle: ICircle, units?: GEOMETRY_UNITS): Feature<Polygon>
  getFeatureFromPolygon(polygon: IPolygon): Feature<Polygon>
  getBboxFromFeature(feature: Feature): IBoundaryBox
  getBboxFromFeatures(features: Feature[]): IBoundaryBox
  getBboxFromPolygon(polygon: IPolygon): IBoundaryBox
  getBboxFromLine(line: ILine): IBoundaryBox
  getPolygonFeatureFromBbox(boundaryBox: IBoundaryBox): Feature<Polygon>
  bboxIntersect(bb1: IBoundaryBox, bb2: IBoundaryBox): boolean
  polygonIntersect(feature1: Feature<Polygon>, feature2: Feature): boolean
  lineIntersect(feature1: Feature<LineString>, feature2: Feature): boolean
  getDistance(p1: IPoint | ILocation, p2: IPoint | ILocation): number // in km
  getFeatureCollection(features: Feature[] | ILocation[] | IPoint[]): FeatureCollection
  getCircleFeature(center: IPoint | ILocation, radius: number, units: DISTANCE_UNITS): Feature<Polygon> // radius in km
  getPolygonFeature(coordinates: IPoint[], properties: any, closeCoordinates?: boolean): Feature<Polygon>
  getMultiPolygonFeature(polygons: Polygon[], properties: any): Feature<MultiPolygon>
  getRotatedFeature(feature: GeoJSON.Feature, angle: number): GeoJSON.Feature
  convertLength(length: number, toUnit: DISTANCE_UNITS, fromUnit?: DISTANCE_UNITS): number // fromUnit is km by default
  convertArea(area: number, toUnit: DISTANCE_UNITS, fromUnit?: DISTANCE_UNITS): number // fromUnit is km by default
  getDestination(origin: ILocation, distance: number, bearing: number): Feature<Point>
}
